import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, usePathname } from 'expo-router';
import { Colors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { authBridge } from '../services/authBridge';

interface DesktopSidebarLayoutProps {
  children: React.ReactNode;
}

export default function DesktopSidebarLayout({ children }: DesktopSidebarLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { width } = useWindowDimensions();
  const isDesktop = width > 768 && Platform.OS === 'web';
  const theme = useAppStore((state) => state.theme);
  const isDark = theme === 'dark';

  const userPhoto = useAppStore((state) => state.userPhoto);
  const userName = useAppStore((state) => state.userName);

  // Anti doble toque: el cierre completo espera a la red (con tope).
  const saliendoRef = React.useRef(false);

  const handleLogout = async () => {
    if (typeof window === 'undefined') return;
    const confirmLogout = window.confirm('¿Estás seguro que deseas cerrar sesión en el portal?');
    if (!confirmLogout) return;
    if (saliendoRef.current) return;
    saliendoRef.current = true;

    // H11, auditoría 16-sep-2026 — Este botón cerraba el SDK web y ponía cuatro
    // campos en null, pero en web AsyncStorage ES `window.localStorage`: el
    // nombre, la foto, el teléfono, el correo y la placa del dueño se quedaban
    // escritos en `go-safely-storage-v2` del navegador. En una PC compartida
    // (un cyber, la oficina del rent car) el siguiente que abría el portal
    // arrancaba con la identidad del anterior. `authBridge.signOut()` es el
    // cierre completo (en web cierra el SDK web y deja el token de push del
    // teléfono en paz) y deja el rol en `null`, no en 'GUEST'.
    try {
      await authBridge.signOut();
    } catch (e) {
      console.warn('Error en signOut:', e);
    } finally {
      saliendoRef.current = false;
    }
    router.replace('/partner');
  };

  // Paridad con el panel del owner en la app móvil. expo-router elimina los
  // grupos `(tabs)` del pathname, por eso aquí usamos las rutas sin prefijo.
  const navItems = [
    { name: 'Dashboard', icon: 'stats-chart', route: '/dashboard' },
    { name: 'Flota e Inventario', icon: 'car', route: '/fleet' },
    { name: 'Choferes', icon: 'people', route: '/drivers' },
    { name: 'Mensajes Internos', icon: 'chatbubbles', route: '/rent-chats' },
    { name: 'Anuncios (Marketplace)', icon: 'pricetag', route: '/marketplace' },
    { name: 'Billetera', icon: 'wallet', route: '/wallet' },
    { name: 'Notificaciones', icon: 'notifications', route: '/notifications' },
    { name: 'CRM & Configuración', icon: 'settings', route: '/crm-settings' },
    { name: 'Mi Perfil', icon: 'person-circle', route: '/edit-profile' },
  ];

  if (!isDesktop) {
    return <>{children}</>;
  }

  return (
    <View style={styles.container}>
      {/* SIDEBAR */}
      <View style={[styles.sidebar, { backgroundColor: isDark ? '#1a1a1a' : '#1C1C1E' }]}>
        <View style={styles.logoContainer}>
          <Ionicons name="shield-checkmark" size={32} color={Colors.primary} />
          <Text style={styles.logoText}>Go Safely</Text>
          <Text style={styles.logoBadge}>PARTNER</Text>
        </View>

        <ScrollView contentContainerStyle={styles.navContainer} showsVerticalScrollIndicator={false}>
          {navItems.map((item) => {
            const isActive = pathname === item.route || pathname?.startsWith(item.route);
            return (
              <TouchableOpacity
                key={item.route}
                style={[styles.navItem, isActive && styles.navItemActive]}
                onPress={() => router.replace(item.route as any)}
              >
                <Ionicons
                  name={(isActive ? item.icon : `${item.icon}-outline`) as any}
                  size={22}
                  color={isActive ? Colors.primary : '#8E8E93'}
                />
                <Text style={[styles.navItemText, isActive && styles.navItemTextActive]}>
                  {item.name}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        <View style={styles.footerContainer}>
          <View style={styles.profileBox}>
            {userPhoto ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={userPhoto} style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} alt="Profile" />
            ) : (
              <View style={[styles.profileAvatarPlaceholder, { backgroundColor: Colors.primary }]}>
                <Text style={{ color: '#FFF', fontWeight: 'bold' }}>
                  {(userName || 'O').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <View style={{ marginLeft: 10, flex: 1 }}>
              <Text style={styles.profileName} numberOfLines={1}>
                {userName || 'Propietario'}
              </Text>
              <Text style={styles.profileRole}>Dueño Rent Car</Text>
            </View>
          </View>

          <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={22} color="#F44336" />
            <Text style={styles.logoutText}>Cerrar Sesión</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* CONTENIDO PRINCIPAL */}
      <View style={styles.content}>
        {children}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    height: '100%',
    overflow: 'hidden',
  },
  sidebar: {
    width: 260,
    height: '100%',
    paddingTop: 30,
    borderRightWidth: 1,
    borderColor: '#333',
    display: 'flex',
    flexDirection: 'column',
  },
  logoContainer: {
    paddingHorizontal: 20,
    marginBottom: 30,
    alignItems: 'flex-start',
  },
  logoText: {
    color: '#FFF',
    fontSize: 24,
    fontWeight: '900',
    marginTop: 10,
  },
  logoBadge: {
    color: Colors.primary,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 2,
    marginTop: 2,
  },
  navContainer: {
    paddingHorizontal: 15,
    paddingBottom: 20,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderRadius: 12,
    marginBottom: 4,
  },
  navItemActive: {
    backgroundColor: 'rgba(255, 107, 0, 0.1)',
  },
  navItemText: {
    color: '#8E8E93',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 12,
  },
  navItemTextActive: {
    color: Colors.primary,
    fontWeight: 'bold',
  },
  footerContainer: {
    padding: 20,
    borderTopWidth: 1,
    borderColor: '#333',
  },
  profileBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  profileAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileName: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  profileRole: {
    color: Colors.primary,
    fontSize: 12,
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
  },
  logoutText: {
    color: '#F44336',
    fontSize: 15,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  content: {
    flex: 1,
    backgroundColor: Colors.background,
    overflow: 'hidden',
  }
});
